// src/pages/DebtStudio/reports/buildIssueReportData.js

import dayjs from "lib/dayjs";
import { buildPrincipalPeriods } from "pages/DebtStudio/lib/buildPrincipalPeriods";
import {
  buildIssueStats,
  computeAverageAnnualDebtService,
  computeIssueAccruedInterest,
  computeLoanDebtServiceMetrics,
  computePricingPV01,
} from "pages/DebtStudio/lib/buildIssueStats";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";
import { buildCostOfIssuanceReport } from "pages/DebtStudio/lib/buildCostOfIssuanceReport";
import { buildRefundingStats } from "pages/DebtStudio/lib/buildRefundingStats";
import {
  fmtCurrency,
  fmtDate,
  fmtNumber,
  fmtTruncNumber,
  fmtPct,
  fmtPctDecimal,
  safeText,
} from "reporting/pdf/pdfFormatters";

function firstValue(...values) {
  for (const value of values) {
    if (value != null && value !== "") return value;
  }
  return null;
}

function hasStatsPayload(stats) {
  return !!(
    stats &&
    typeof stats === "object" &&
    !Array.isArray(stats) &&
    Object.keys(stats).length > 0
  );
}

function fmtPercentPointsOrDash(value, digits = 3) {
  if (value == null || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  return fmtPct(n, digits);
}

function fmtDecimalPctOrDash(value, digits = 3) {
  if (value == null || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  return fmtPctDecimal(n, digits);
}

function fmtNumberOrDash(value, digits = 3, suffix = "") {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${fmtNumber(n, digits)}${suffix}`;
}

function fmtCurrencyOrDash(value, digits = 2) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return fmtCurrency(n, digits);
}

function safeIso(d) {
  const x = d ? dayjs(d) : null;
  return x && x.isValid() ? x.format("YYYY-MM-DD") : "";
}

function ratePctToDec(x) {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n <= 1 ? n : n / 100;
}

function earningsYearBasisFromDayCount(dayCount) {
  const dc = String(dayCount || "").toUpperCase();
  if (dc.includes("ACT/360")) return 360;
  return 365;
}

function yearFracACT(d0, d1, basis) {
  const a = dayjs(d0);
  const b = dayjs(d1);
  if (!a.isValid() || !b.isValid()) return 0;

  const days = b.diff(a, "day");
  if (!Number.isFinite(days) || days <= 0) return 0;

  return days / (basis || 365);
}

function getAdjustment(row) {
  return Number(row?.debtServiceAdjustment ?? row?.adjustment ?? 0) || 0;
}

function getNetDebtService(row) {
  const explicit = Number(row?.netDebtService);
  if (Number.isFinite(explicit)) return explicit;

  const gross = Number(row?.total) || 0;
  return gross + getAdjustment(row);
}

function getExistingDebtService(row) {
  const explicit = Number(
    row?.existingCashFlows ??
      row?.existingDebtService ??
      row?.baseDebtService ??
      row?.existing_debt_service ??
      row?.existing_ds ??
      row?.existingDS ??
      0
  );

  return Number.isFinite(explicit) ? explicit : 0;
}

function getAggregateDebtService(row) {
  const explicit = Number(
    row?.aggregateCashFlows ??
      row?.aggregateDebtService ??
      row?.aggregateNetDebtService ??
      row?.aggregate_debt_service ??
      row?.aggregate_ds ??
      row?.aggregateDS
  );

  if (Number.isFinite(explicit)) return explicit;

  return getNetDebtService(row) + getExistingDebtService(row);
}

function hasNonzeroExistingDebtService(rows = []) {
  return rows.some((r) => Math.abs(getExistingDebtService(r)) > 0.005);
}

function hasNonzeroAdjustment(rows = []) {
  return rows.some((r) => Math.abs(getAdjustment(r)) > 0.005);
}

function maxDate(rows) {
  let max = null;

  for (const r of rows || []) {
    const d = dayjs(r.date);
    if (!max || d.isAfter(max)) max = d;
  }

  return max;
}

function formatTargetType(value, isLoan = false) {
  const s = String(value || "").trim();

  if (!s) return "—";

  const labels = {
    par_amount: isLoan ? "Principal Amount" : "Par Amount",
    net_proceeds: "Net Proceeds",
    gross_proceeds: "Gross Proceeds",
    periodic_ds: "Periodic Debt Service",
    level_ds: "Level Debt Service",
  };

  return labels[s] || s.replace(/_/g, " ");
}

function formatFundingType(value) {
  const s = String(value || "").trim();

  if (!s) return "—";

  const labels = {
    gross: "Gross Funding",
    net: "Net Funding",
  };

  return labels[s] || s.replace(/_/g, " ");
}

function formatOnOff(value) {
  if (value === true) return "On";
  if (value === false) return "Off";
  if (value == null || value === "") return "—";

  return String(value);
}

function groupDebtServiceForPdf({ rows = [], form = {} }) {
  const cashflows = Array.isArray(rows) ? rows : [];

  const datedDate = firstValue(form.datedDate, form.dated_date);

  const principalFrequency =
    firstValue(form.principalFrequency, form.principal_frequency) || "annual";

  const firstPrincipalDate = firstValue(
    form.firstPrincipalDate,
    form.first_principal_date
  );

  if (!cashflows.length || !datedDate || !firstPrincipalDate) {
    return cashflows.map((r) => {
      const existing = getExistingDebtService(r);

      return {
        ...r,
        existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
        aggregateCashFlows: getAggregateDebtService(r),
        periodTotal: null,
      };
    });
  }

  const last = maxDate(cashflows);

  const allPeriods = buildPrincipalPeriods({
    startDate: datedDate,
    periodEndAnchor: firstPrincipalDate,
    lastCashflowDate: last,
    principalFrequency,
  });

  if (!allPeriods.length) {
    return cashflows.map((r) => {
      const existing = getExistingDebtService(r);

      return {
        ...r,
        existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
        aggregateCashFlows: getAggregateDebtService(r),
        periodTotal: null,
      };
    });
  }

  const periods = allPeriods;

  const totals = periods.map((p) => ({
    start: p.start,
    end: p.end,
    netTotal: 0,
    lastRowIndex: -1,
  }));

  const out = cashflows.map((r) => {
    const existing = getExistingDebtService(r);

    return {
      ...r,
      existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
      aggregateCashFlows: getAggregateDebtService(r),
      periodTotal: null,
    };
  });

  const useAggregatePeriodTotal = hasNonzeroExistingDebtService(out);

  for (let i = 0; i < out.length; i++) {
    const row = out[i];
    const payDate = dayjs(row.date);

    const idx = totals.findIndex(
      (p) =>
        payDate.isAfter(p.start, "day") &&
        (payDate.isBefore(p.end, "day") || payDate.isSame(p.end, "day"))
    );

    if (idx >= 0) {
      totals[idx].netTotal += useAggregatePeriodTotal
        ? getAggregateDebtService(row)
        : getNetDebtService(row);

      totals[idx].lastRowIndex = i;
    }
  }

  for (const p of totals) {
    if (p.lastRowIndex >= 0) {
      out[p.lastRowIndex] = {
        ...out[p.lastRowIndex],
        periodTotal: p.netTotal,
      };
    } else {
      out.push({
        date: p.end.format("YYYY-MM-DD"),
        principal: null,
        interest: null,
        total: null,
        debtServiceAdjustment: null,
        adjustment: null,
        netDebtService: null,
        existingCashFlows: null,
        existingDebtService: null,
        existing_ds: null,
        existingDS: null,
        aggregateCashFlows: null,
        aggregateDebtService: null,
        aggregate_ds: null,
        aggregateDS: null,
        periodTotal: 0,
      });
    }
  }

  out.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

  return out;
}

function buildDebtServiceChartRows({ rows = [], form = {} }) {
  const cashflows = Array.isArray(rows) ? rows : [];

  const couponData = cashflows.map((r) => ({
    date: r.date,
    principal: Number(r.principal) || 0,
    interest: Number(r.interest) || 0,
    existingDebtService: null,
    aggregateDebtService: null,
    periodTotal: null,
  }));

  const datedDate = firstValue(form.datedDate, form.dated_date);
  const firstPrincipalDate = firstValue(
    form.firstPrincipalDate,
    form.first_principal_date
  );
  const principalFrequency =
    firstValue(form.principalFrequency, form.principal_frequency) || "annual";

  if (!cashflows.length || !datedDate || !firstPrincipalDate) {
    return couponData;
  }

  const last = maxDate(cashflows);
  const allPeriods = buildPrincipalPeriods({
    startDate: datedDate,
    periodEndAnchor: firstPrincipalDate,
    lastCashflowDate: last,
    principalFrequency,
  });

  if (!allPeriods.length) return couponData;

  const periods = allPeriods;

  const periodTotals = periods
    .map((p) => {
      const totals = cashflows.reduce(
        (acc, r) => {
          const d = dayjs(r.date);
          const inPeriod =
            d.isAfter(p.start, "day") &&
            (d.isBefore(p.end, "day") || d.isSame(p.end, "day"));

          if (!inPeriod) return acc;

          acc.existing += getExistingDebtService(r);
          acc.aggregate += getAggregateDebtService(r);

          return acc;
        },
        {
          existing: 0,
          aggregate: 0,
        }
      );

      return {
        date: p.end.format("YYYY-MM-DD"),
        existingDebtService:
          Math.abs(totals.existing) > 0.005 ? totals.existing : null,
        periodTotal: Math.abs(totals.aggregate) > 0.005 ? totals.aggregate : null,
      };
    });

  const map = new Map();

  for (const row of couponData) {
    map.set(row.date, row);
  }

  for (const row of periodTotals) {
    if (map.has(row.date)) {
      map.set(row.date, {
        ...map.get(row.date),
        existingDebtService: row.existingDebtService,
        periodTotal: row.periodTotal,
      });
    } else {
      map.set(row.date, {
        date: row.date,
        principal: 0,
        interest: 0,
        existingDebtService: row.existingDebtService,
        aggregateDebtService: row.periodTotal,
        periodTotal: row.periodTotal,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
}

function buildCapitalizedInterestSection({ form = {}, rows = [] }) {
  const dealType = form?.deal_type || form?.dealType;
  const capiEnabled = form?.capiEnabled ?? form?.capi_enabled ?? false;

  const capi = computeCapI({
    dealType,
    capiEnabled,
    capiDate: form?.capiDate ?? form?.capi_date ?? "",
    capiInvestmentRate:
      form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0,
    rows,
    form: {
      datedDate: form?.datedDate ?? form?.dated_date ?? null,
      deliveryDate: form?.deliveryDate ?? form?.delivery_date ?? null,
      firstCouponDate: form?.firstCouponDate ?? form?.first_coupon_date ?? null,
      finalMaturityDate:
        form?.finalMaturityDate ?? form?.final_maturity_date ?? null,
      couponFrequency:
        form?.couponFrequency ?? form?.coupon_frequency ?? "semiannual",
      dayCount: form?.dayCount ?? form?.day_count ?? "30/360",
    },
  });

  if (!capi?.enabled) {
    return {
      show: false,
      kpis: [],
      table: null,
    };
  }

  const deliveryISO =
    safeIso(form?.deliveryDate ?? form?.delivery_date) ||
    safeIso(form?.datedDate ?? form?.dated_date) ||
    "";

  const capiRatePct =
    Number(form?.capiInvestmentRate ?? form?.capi_investment_rate ?? 0) || 0;

  const rateDec = ratePctToDec(capiRatePct);

  const yearBasis = earningsYearBasisFromDayCount(
    form?.dayCount ?? form?.day_count
  );

  const initialDeposit = Number(capi?.capiUse || 0);
  const flows = Array.isArray(capi?.flowsIncluded) ? capi.flowsIncluded : [];

  const pays = flows
    .map((f) => ({
      date: safeIso(f?.date) || String(f?.date || ""),
      interest: Number(f?.interest || 0),
    }))
    .filter((x) => !!x.date)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const data = [];

  if (deliveryISO && initialDeposit > 0) {
    data.push({
      date: deliveryISO,
      type: "Inflow",
      description: "Initial Cap-I Deposit",
      inflow: initialDeposit,
      outflow: 0,
      netChange: initialDeposit,
      _sortKey: 0,
    });
  }

  let bal = initialDeposit;
  let lastDate = deliveryISO || pays[0]?.date || "";

  let totalEarnings = 0;
  let totalInterestPaid = 0;
  let minBalance = bal;

  for (const p of pays) {
    const payDate = p.date;

    const yf = yearFracACT(lastDate, payDate, yearBasis);
    let earned = 0;

    if (rateDec > 0 && yf > 0 && bal > 0) {
      earned = bal * rateDec * yf;
    }

    earned = Number.isFinite(earned) ? earned : 0;

    if (earned > 0.0000001) {
      totalEarnings += earned;
      bal += earned;

      data.push({
        date: payDate,
        type: "Inflow",
        description: "Cap-I Earnings (estimated)",
        inflow: earned,
        outflow: 0,
        netChange: earned,
        _sortKey: 1,
      });
    }

    const payInt = Number(p.interest || 0);
    const out = Number.isFinite(payInt) && payInt > 0 ? payInt : 0;

    if (out > 0) {
      totalInterestPaid += out;
      bal -= out;

      data.push({
        date: payDate,
        type: "Outflow",
        description: "Interest Payment",
        inflow: 0,
        outflow: out,
        netChange: -out,
        _sortKey: 2,
      });
    }

    if (bal < minBalance) minBalance = bal;
    lastDate = payDate;
  }

  data.sort((a, b) => {
    const ad = a.date || "";
    const bd = b.date || "";

    if (ad !== bd) return ad < bd ? -1 : 1;

    const ak = a._sortKey ?? 9;
    const bk = b._sortKey ?? 9;

    return ak - bk;
  });

  let running = 0;

  const ledger = data.map((r) => {
    running += r.netChange || 0;
    minBalance = Math.min(minBalance, running);

    return {
      date: r.date,
      type: r.type,
      description: r.description,
      inflow: r.inflow || 0,
      outflow: r.outflow || 0,
      netChange: r.netChange || 0,
      balance: running,
    };
  });

  const endingBalance = ledger.length ? ledger[ledger.length - 1].balance : 0;

  return {
    show: ledger.length > 0,
    kpis: [
      {
        label: "Initial Deposit",
        value: fmtCurrencyOrDash(initialDeposit, 2),
      },
      {
        label: "Total Interest Paid",
        value: fmtCurrencyOrDash(totalInterestPaid, 2),
      },
      {
        label: "Estimated Earnings",
        value: fmtCurrencyOrDash(totalEarnings, 2),
      },
      {
        label: "Ending Balance",
        value: fmtCurrencyOrDash(endingBalance, 2),
      },
      {
        label: "Investment Rate",
        value: fmtPercentPointsOrDash(capiRatePct, 3),
      },
      {
        label: "Earnings Basis",
        value: `ACT/${yearBasis}`,
      },
    ],
    table: {
      title: "Cap-I Ledger",
      columns: [
        {
          key: "date",
          label: "Date",
          align: "left",
          format: (v) => fmtDate(v),
        },
        {
          key: "type",
          label: "Type",
          align: "left",
        },
        {
          key: "description",
          label: "Description",
          align: "left",
        },
        {
          key: "inflow",
          label: "Inflow",
          align: "right",
          format: (v) => fmtCurrencyOrDash(v, 2),
        },
        {
          key: "outflow",
          label: "Outflow",
          align: "right",
          format: (v) => fmtCurrencyOrDash(v, 2),
        },
        {
          key: "netChange",
          label: "Net Change",
          align: "right",
          format: (v) => fmtCurrencyOrDash(v, 2),
        },
        {
          key: "balance",
          label: "Balance",
          align: "right",
          format: (v) => fmtCurrencyOrDash(v, 2),
        },
      ],
      rows: ledger,
    },
  };
}

export function buildIssueReportData({
  form = {},
  rows = [],
  debtServiceRows = [],
  refundingComparison = [],
  sourcesUses = null,
  stats = null,
  solution = {},
  orgName = "",
  issueName = "",
  preparedDate = new Date(),
} = {}) {
  const isLoan =
    String(firstValue(form?.financingType, form?.financing_type) || "")
      .trim()
      .toLowerCase() === "loan";
  const finalPar = rows.reduce(
    (sum, row) => sum + (Number(row?.principal) || 0),
    0
  );
  const costOfIssuance = buildCostOfIssuanceReport(
    form?.costOfIssuance ?? form?.cost_of_issuance ?? [],
    finalPar
  );

  let computedStats = hasStatsPayload(stats)
    ? stats
    : buildIssueStats({
        form,
        bondRows: rows,
        flowRows: debtServiceRows,
      }) || {};

  if (!isLoan && computedStats.pv01 == null) {
    computedStats = {
      ...computedStats,
      pv01: computePricingPV01({
        bondRows: rows,
        deliveryDate: firstValue(
          form.deliveryDate,
          form.delivery_date,
          form.datedDate,
          form.dated_date
        ),
        dayCount: firstValue(form.dayCount, form.day_count) || "30/360",
        couponFrequency:
          firstValue(form.couponFrequency, form.coupon_frequency) ||
          "semiannual",
      }),
    };
  }

  if (debtServiceRows.length > 0) {
    const startDate = firstValue(
      form.deliveryDate,
      form.delivery_date,
      form.datedDate,
      form.dated_date
    );
    const endDate = debtServiceRows[debtServiceRows.length - 1]?.date;
    const totalDebtService = debtServiceRows.reduce(
      (sum, row) => sum + (Number(row?.total) || 0),
      0
    );
    const accruedInterest = computeIssueAccruedInterest({
      bondRows: rows,
      datedDate: firstValue(form.datedDate, form.dated_date),
      deliveryDate: firstValue(form.deliveryDate, form.delivery_date),
      dayCount: firstValue(form.dayCount, form.day_count) || "30/360",
    });

    if (isLoan) {
      const loanMetrics = computeLoanDebtServiceMetrics({
        schedule: debtServiceRows,
        form,
        startDate,
        dayCount: firstValue(form.dayCount, form.day_count) || "30/360",
      });
      computedStats = {
        ...computedStats,
        avgAnnualDS: loanMetrics.recurringAvgAnnualDS,
        maxAnnualDS: null,
        recurringTotalDebtService: loanMetrics.recurringTotalDebtService,
        hasBalloon: loanMetrics.hasBalloon,
        balloonPrincipal: loanMetrics.balloonPrincipal,
        incrementalBalloonPrincipal:
          loanMetrics.incrementalBalloonPrincipal,
      };
    } else {
      computedStats = {
        ...computedStats,
        avgAnnualDS: computeAverageAnnualDebtService({
          totalDebtService,
          accruedInterest,
          startDate,
          endDate,
          dayCount: firstValue(form.dayCount, form.day_count) || "30/360",
        }),
      };
    }
  }

  const issuer =
    firstValue(
      form.issuerNameRaw,
      form.issuer_name_raw,
      form.issuer_name,
      form.issuer,
      form.issuerName
    ) || "Issuer";

  const title =
    firstValue(
      issueName,
      form.dealName,
      form.name,
      form.issue_name,
      form.issueName,
      form.title
    ) || "DebtStudio - Issue Report";

  const datedDate = firstValue(form.datedDate, form.dated_date);
  const deliveryDate = firstValue(form.deliveryDate, form.delivery_date);

  const finalMaturity =
    firstValue(form.finalMaturityDate, form.final_maturity_date) ||
    (rows.length > 0
      ? rows
          .map((r) => firstValue(r.date, r.maturity, r.maturity_date))
          .filter(Boolean)
          .sort()
          .at(-1)
      : null);

  const groupedDebtServiceRows = groupDebtServiceForPdf({
    rows: debtServiceRows,
    form,
  });

  const debtServiceChartRows = buildDebtServiceChartRows({
    rows: debtServiceRows,
    form,
  });

  const showAdjustmentColumns = hasNonzeroAdjustment(groupedDebtServiceRows);

  const showExistingDebtServiceColumns =
    hasNonzeroExistingDebtService(groupedDebtServiceRows);

  const existingDebtServiceTotal = groupedDebtServiceRows.reduce(
    (sum, r) => sum + getExistingDebtService(r),
    0
  );

  const aggregateDebtServiceTotal = groupedDebtServiceRows.reduce(
    (sum, r) => sum + getAggregateDebtService(r),
    0
  );

  const debtServiceKpis = [
    {
      label: "Total Principal",
      value: fmtCurrencyOrDash(computedStats.totalPrincipal, 2),
    },
    {
      label: "Total Interest",
      value: fmtCurrencyOrDash(computedStats.totalInterest, 2),
    },
    {
      label: "Total Debt Service",
      value: fmtCurrencyOrDash(computedStats.totalDebtService, 2),
    },

    ...(showAdjustmentColumns
      ? [
          {
            label: "Total Adjustments",
            value: fmtCurrencyOrDash(
              groupedDebtServiceRows.reduce(
                (sum, r) => sum + getAdjustment(r),
                0
              ),
              2
            ),
          },
          {
            label: "Total Net Debt Service",
            value: fmtCurrencyOrDash(
              groupedDebtServiceRows.reduce(
                (sum, r) => sum + getNetDebtService(r),
                0
              ),
              2
            ),
          },
        ]
      : []),

    ...(showExistingDebtServiceColumns
      ? [
          {
            label: "Total Existing D/S",
            value: fmtCurrencyOrDash(existingDebtServiceTotal, 2),
          },
          {
            label: "Total Combined D/S",
            value: fmtCurrencyOrDash(aggregateDebtServiceTotal, 2),
          },
        ]
      : []),
  ];

  const capitalizedInterest = buildCapitalizedInterestSection({
    form,
    rows,
  });

  const isRefunding =
    String(firstValue(form?.dealType, form?.deal_type) || "").toLowerCase() ===
    "refunding";
  const refundingStats = isRefunding
    ? buildRefundingStats({
        form,
        bondRows: rows,
        comparison: refundingComparison,
        discountRate: computedStats.arbYield,
      })
    : null;
  const refundingStatisticsSections = refundingStats
    ? [
        {
          title: "Refunding Results",
          rows: [
            {
              label: "Prior Debt Service",
              value: fmtCurrencyOrDash(refundingStats.priorDebtService, 2),
            },
            {
              label: "Refunding Debt Service",
              value: fmtCurrencyOrDash(refundingStats.refundingDebtService, 2),
            },
            {
              label: "Gross Cash-Flow Savings",
              value: fmtCurrencyOrDash(refundingStats.grossCashFlowSavings, 2),
            },
            {
              label: "Net PV Savings / Savings % (preliminary)",
              value: `${fmtCurrencyOrDash(
                refundingStats.preliminaryNetPvSavings,
                2
              )} / ${fmtDecimalPctOrDash(
                refundingStats.preliminarySavingsPctOfRefundedPar,
                3
              )}`,
              bold: true,
            },
            {
              note: `PV discounted to delivery at bond yield of ${fmtDecimalPctOrDash(
                refundingStats.discountRate,
                6
              )}.`,
            },
            {
              note: "Preliminary net assumes zero prior/refunding funds-on-hand adjustments and excludes escrow effects.",
            },
          ],
        },
      ]
    : [];

  const refundingComparisonRows = isRefunding && Array.isArray(refundingComparison)
    ? refundingComparison
    : [];
  const refundingComparisonTotals = refundingComparisonRows.reduce(
    (totals, row) => ({
      priorDebtService:
        totals.priorDebtService + (Number(row?.priorDebtService) || 0),
      refundingDebtService:
        totals.refundingDebtService + (Number(row?.refundingDebtService) || 0),
      savings: totals.savings + (Number(row?.savings) || 0),
    }),
    { priorDebtService: 0, refundingDebtService: 0, savings: 0 }
  );

  const issueStatisticsSections = [
    {
      title: "General",
      rows: [
        { label: isLoan ? "Loan Date" : "Dated Date", value: fmtDate(datedDate) },
        ...(!isLoan
          ? [{ label: "Delivery Date", value: fmtDate(deliveryDate) }]
          : []),
        { label: "First Payment", value: fmtDate(computedStats.firstPayment) },
        { label: "Final Maturity", value: fmtDate(finalMaturity) },
      ],
    },
    {
      title: isLoan ? "Loan Metrics" : "Issue Metrics",
      rows: [
        {
          label: isLoan ? "Weighted Avg Interest Rate" : "Average Coupon",
          value: fmtDecimalPctOrDash(computedStats.wac, 3),
        },
        ...(!isLoan
          ? [
              {
                label: "Weighted Avg Price",
                value: fmtNumberOrDash(computedStats.wap, 9),
              },
            ]
          : []),
        {
          label: "Weighted Average Life",
          value: fmtNumberOrDash(computedStats.wal, 4, " yrs"),
        },
        ...(!isLoan
          ? [
              {
                label: "Duration (Macaulay)",
                value: fmtNumberOrDash(
                  computedStats.macaulayDuration,
                  4,
                  " yrs"
                ),
              },
              {
                label: "PV01",
                value: fmtCurrencyOrDash(computedStats.pv01, 2),
              },
            ]
          : []),
      ],
    },
    {
      title: "Debt Service Summary",
      rows: [
        {
          label: "Total Principal",
          value: fmtCurrencyOrDash(computedStats.totalPrincipal, 2),
        },
        {
          label: "Total Interest",
          value: fmtCurrencyOrDash(computedStats.totalInterest, 2),
        },
        {
          label: "Total Debt Service",
          value: fmtCurrencyOrDash(computedStats.totalDebtService, 2),
          bold: true,
        },
        {
          label: isLoan
            ? "Avg Annual Debt Service (Recurring)"
            : "Avg Annual Debt Service",
          value: fmtCurrencyOrDash(computedStats.avgAnnualDS, 2),
        },
        ...(!isLoan
          ? [
              {
                label: "Max Annual Debt Service",
                value: fmtCurrencyOrDash(computedStats.maxAnnualDS, 2),
              },
            ]
          : []),
        ...(isLoan && computedStats.hasBalloon
          ? [
              {
                label: "Balloon Principal (at Maturity)",
                value: fmtCurrencyOrDash(computedStats.balloonPrincipal, 2),
              },
              {
                note: "Recurring AADS excludes Balloon Principal.",
              },
            ]
          : []),
      ],
    },
    ...(!isLoan
      ? [
          {
            title: "Yield Metrics",
            rows: [
              {
                label: "Arbitrage Yield",
                value: fmtDecimalPctOrDash(computedStats.arbYield, 6),
              },
              {
                label: "True Interest Cost",
                value: fmtDecimalPctOrDash(computedStats.tic, 6),
              },
              {
                label: "Net Interest Cost",
                value: fmtDecimalPctOrDash(computedStats.nic, 6),
              },
              {
                label: "All-In Cost",
                value: fmtDecimalPctOrDash(computedStats.aic, 6),
              },
            ],
          },
        ]
      : []),
  ];

  const structureColumns = [
    {
      key: "date",
      label: isLoan ? "Payment Date" : "Maturity",
      align: "left",
      format: (v, row) => fmtDate(firstValue(v, row.maturity, row.maturity_date)),
    },
    {
      key: "principal",
      label: "Principal",
      align: "right",
      format: (v, row) =>
        fmtCurrencyOrDash(firstValue(v, row.par, row.par_amount), 0),
    },
    {
      key: "coupon",
      label: isLoan ? "Interest Rate" : "Coupon",
      align: "right",
      format: (v) => fmtPercentPointsOrDash(v, 3),
    },
    ...(!isLoan
      ? [
          {
            key: "yield",
            label: "Yield",
            align: "right",
            format: (v) => fmtPercentPointsOrDash(v, 3),
          },
          {
            key: "price",
            label: "Price",
            align: "right",
            format: (v) => {
              if (v == null || v === "") return "—";

              const n = Number(v);
              if (!Number.isFinite(n)) return "—";

              return fmtTruncNumber(n, 3);
            },
          },
          {
            key: "bond_type",
            label: "Type",
            align: "left",
            format: (v, row) => safeText(firstValue(v, row.type)),
          },
          {
            key: "call_date",
            label: "Call Date",
            align: "left",
            format: (v) => fmtDate(v),
          },
        ]
      : []),
  ];

  const debtServiceColumns = [
    {
      key: "date",
      label: "Date",
      align: "left",
      format: (v, row) => fmtDate(firstValue(v, row.paymentDate, row.payment_date)),
    },
    ...(isLoan
      ? [
          {
            key: "beginningBalance",
            label: "Beginning Balance",
            align: "right",
            format: (v) =>
              v == null || v === "" ? "" : fmtCurrencyOrDash(v, 2),
          },
          {
            key: "rate",
            label: "Rate",
            align: "right",
            format: (v, row) => {
              if (v == null || v === "") return "";

              const rate = Number(v);
              const interest = Number(row?.interest);

              return Number.isFinite(rate) &&
                Number.isFinite(interest) &&
                Math.abs(interest) > 0.005
                ? fmtPct(rate, 3)
                : "";
            },
          },
        ]
      : []),
    {
      key: "principal",
      label: "Principal",
      align: "right",
      format: (v) => fmtCurrencyOrDash(v, 2),
    },
    {
      key: "interest",
      label: "Interest",
      align: "right",
      format: (v) => fmtCurrencyOrDash(v, 2),
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      format: (v, row) =>
        fmtCurrencyOrDash(firstValue(v, row.totalDebtService, row.total_ds), 2),
    },
    ...(showAdjustmentColumns
      ? [
          {
            key: "debtServiceAdjustment",
            label: "Adjustments",
            align: "right",
            format: (v, row) =>
              fmtCurrencyOrDash(firstValue(v, row.adjustment), 2),
          },
          {
            key: "netDebtService",
            label: "Net Debt Service",
            align: "right",
            format: (v, row) =>
              fmtCurrencyOrDash(firstValue(v, getNetDebtService(row)), 2),
          },
        ]
      : []),

    ...(showExistingDebtServiceColumns
      ? [
          {
            key: "existingCashFlows",
            label: "Existing D/S",
            align: "right",
            format: (v) => {
              const n = Number(v);
              return Number.isFinite(n) && Math.abs(n) > 0.005
                ? fmtCurrencyOrDash(n, 2)
                : "";
            },
          },
        ]
      : []),
    {
      key: "periodTotal",
      label: "Period Total",
      align: "right",
      format: (v) => {
        if (v == null || v === "") return "";
        return fmtCurrencyOrDash(v, 2);
      },
    },
  ];

  const solutionRows = [
    {
      label: "Target Type",
      value: formatTargetType(
        firstValue(solution?.targetType, form.targetType, form.target_type),
        isLoan
      ),
    },
    {
      label: "Target Value",
      value: fmtCurrencyOrDash(firstValue(form.targetValue, form.target_value), 2),
    },
    {
      label: "Principal Frequency",
      value: safeText(
        firstValue(form.principalFrequency, form.principal_frequency)
      ),
    },
    {
      label: "Interest Frequency",
      value: safeText(
        firstValue(form.couponFrequency, form.coupon_frequency)
      ),
    },
    {
      label: "Day Count",
      value: safeText(firstValue(form.dayCount, form.day_count)),
    },
    {
      label: "Min. Increment/Denomination",
      value: fmtCurrencyOrDash(
        firstValue(solution?.denomination, form.denomination),
        2
      ),
    },

    ...(!isLoan
      ? [
          {
            label: "Target Gross",
            value: fmtCurrencyOrDash(
              firstValue(form.targetGross, form.target_gross),
              2
            ),
          },
          {
            label: "Funding Type",
            value: formatFundingType(
              firstValue(form.fundingType, form.funding_type)
            ),
          },
          {
            label: "Capitalized Interest",
            value: formatOnOff(firstValue(form.capiEnabled, form.capi_enabled)),
          },
          {
            label: "Cap-I Date",
            value: fmtDate(firstValue(form.capiDate, form.capi_date)),
          },
          {
            label: "Cap-I Investment Rate",
            value: fmtPercentPointsOrDash(
              firstValue(form.capiInvestmentRate, form.capi_investment_rate),
              3
            ),
          },
          {
            label: "Purchase Price",
            value: fmtCurrencyOrDash(computedStats.purchasePrice, 2),
          },
          {
            label: "Underwriter's Discount",
            value: fmtCurrencyOrDash(computedStats.underwriterDiscount, 2),
          },
          {
            label: "Accrued Interest",
            value: fmtCurrencyOrDash(computedStats.accruedInterest, 2),
          },
          {
            label: "Arbitrage Yield Included Costs",
            value: fmtCurrencyOrDash(computedStats.arbYieldCosts, 2),
          },
          {
            label: "TIC Included Costs",
            value: fmtCurrencyOrDash(computedStats.ticCosts, 2),
          },
          {
            label: "AIC Included Costs",
            value: fmtCurrencyOrDash(computedStats.aicCosts, 2),
          },
        ]
      : [
          {
            label: "Amortization",
            value:
              Number(
                firstValue(
                  form.amortizationPeriods,
                  form.amortization_periods
                )
              ) >
              Number(
                firstValue(form.loanTermPeriods, form.loan_term_periods)
              )
                ? "balloon"
                : "fully amortizing",
          },
          {
            label: "Loan Term Periods",
            value:
              firstValue(form.loanTermPeriods, form.loan_term_periods) != null
                ? String(firstValue(form.loanTermPeriods, form.loan_term_periods))
                : "—",
          },
          {
            label: "Amortization Periods",
            value:
              firstValue(form.amortizationPeriods, form.amortization_periods) !=
              null
                ? String(
                    firstValue(
                      form.amortizationPeriods,
                      form.amortization_periods
                    )
                  )
                : "—",
          },
        ]),
  ];

  const solutionColumns = [
    {
      key: "label",
      label: "Parameter",
      align: "left",
    },
    {
      key: "value",
      label: "Value",
      align: "right",
    },
  ];

  return {
    meta: {
      reportTitle: "DebtStudio - Issue Report",
      orgName,
      issuer,
      issueName: title,
      asOfLabel: `Prepared ${dayjs(preparedDate).format("M/D/YYYY")}`,
      fileDate: dayjs(preparedDate).format("YYYY-MM-DD"),
      isLibraryIssue:
        String(form?.dealClass || form?.deal_class || "").toLowerCase() ===
        "library",
      financingType: isLoan ? "loan" : "bond",
      isLoan,
    },

    sourcesUses,

    capitalizedInterest,

    issueStatisticsSections,

    refundingStatisticsSections,

    stats: computedStats,

    structureTable: {
      show: !isLoan,
      title: "Bond Summary",
      columns: structureColumns,
      rows: isLoan ? [] : rows,
    },

    debtServiceKpis,

    debtServiceTable: {
      title: "Debt Service",
      columns: debtServiceColumns,
      rows: groupedDebtServiceRows,
    },

    debtServiceChart: {
      rows: debtServiceChartRows,
    },

    refundingComparisonTable: refundingComparisonRows.length
      ? {
          title: "Prior vs. Refunding Debt Service",
          columns: [
            { key: "date", label: "Date", align: "left", format: fmtDate },
            {
              key: "priorDebtService",
              label: "Prior Debt Service",
              align: "right",
              format: (value) => fmtCurrencyOrDash(value, 2),
            },
            {
              key: "refundingDebtService",
              label: "Refunding Debt Service",
              align: "right",
              format: (value) => fmtCurrencyOrDash(value, 2),
            },
            {
              key: "savings",
              label: "Savings",
              align: "right",
              format: (value) => fmtCurrencyOrDash(value, 2),
            },
          ],
          rows: refundingComparisonRows,
          foot: [
            "Total",
            fmtCurrencyOrDash(refundingComparisonTotals.priorDebtService, 2),
            fmtCurrencyOrDash(refundingComparisonTotals.refundingDebtService, 2),
            fmtCurrencyOrDash(refundingComparisonTotals.savings, 2),
          ],
          showFoot: "lastPage",
          didParseCell: (cellData) => {
            if (cellData.section === "foot" && cellData.column.index > 0) {
              cellData.cell.styles.halign = "right";
            }
          },
        }
      : null,

    costOfIssuanceTable: {
      title: "Cost of Issuance",
      columns: [
        { key: "label", label: "Cost of Issuance", align: "left" },
        {
          key: "percentOfPar",
          label: "% of Par",
          align: "right",
          format: (value) =>
            value == null ? "" : Number(value).toFixed(5),
        },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          format: (value) => fmtCurrency(value, 2),
        },
      ],
      rows: costOfIssuance.rows,
      foot: [
        "Total",
        costOfIssuance.total.percentOfPar == null
          ? ""
          : Number(costOfIssuance.total.percentOfPar).toFixed(5),
        fmtCurrency(costOfIssuance.total.amount, 2),
      ],
      didParseCell: (cellData) => {
        if (cellData.section === "foot" && cellData.column.index > 0) {
          cellData.cell.styles.halign = "right";
        }
      },
    },

    solutionTable: {
      title: isLoan ? "Loan Solution Parameters" : "Bond Solution Parameters",
      columns: solutionColumns,
      rows: solutionRows,
    },
  };
}
